import './auditlog.less';

import _ from 'lodash';
import { auditIncidents } from '../../objects/Audit';

/**
 * @typedef { import('../../objects/Audit').AuditData } AuditData
 * @typedef { import('../../../common/account/UserData').UserData } UserData
 */

/**
 * @template T, V
 * @param {T[]} entries 
 * @param {(T) => V} trimWith
 * @param {V[]} trimValues 
 * @returns {T[]}
 */
function trimRepeats(entries, trimWith, trimValues) {
  if (entries.length === 0) return [];
  
  let previousValue = trimWith(entries[0]);
  let result = [entries[0]];

  for (let ii=1; ii<entries.length; ++ii) {
      let item = entries[ii];
      let value = trimWith(item);
      if (value === previousValue && trimValues.includes(value)) {
          continue;
      }
      previousValue = value;
      result.push(item);
  }

  return result;
}
/**
 * @param {AuditData} auditData
 * @param {Map<string, UserData>} users
 */
function getUserName(auditData, users){
  if (auditData.UserId) {
    let user = users.get(auditData.UserId);
    if (user) {
        return user.displayName || 'Okänd användare';
    } else {
        return 'Okänd användare';
    }
  } else if (auditData.PersonId) {
    return auditData.Person?.display;
  } else {
      return null;
  }
}

function getIncidentText(incident) {
  switch (incident) {
    case auditIncidents.ceremonyReported: return 'Rapporterad';
    case auditIncidents.ceremonyRejected: return 'Avslagen';
    case auditIncidents.ceremonyAccepted: return 'Godkänd';
    case auditIncidents.ceremonyReset: return 'Närvaro borttagen';
    case auditIncidents.timeRequested: return 'Begärd';
    case auditIncidents.timeReported: return 'Rapporterad';
    case auditIncidents.timeRejected: return 'Avslagen';
    case auditIncidents.timeAccepted: return 'Godkänd';
    case auditIncidents.timeRecalled: return 'Återkallad';
    case auditIncidents.timeRemoved: return 'Återkallad';
    case auditIncidents.detachedMission: return 'Uppdrag borttaget';
    case auditIncidents.detachedEvent: return 'Närvaro borttagen';
    case auditIncidents.claimReported: return 'Rapporterad';
    case auditIncidents.claimRejected: return 'Avslagen';
    case auditIncidents.claimReset: return 'Återställd';
    case auditIncidents.claimAccepted: return 'Godkänd';
    case auditIncidents.claimRecalled: return 'Återkallad';
    case auditIncidents.certificationRequested: return 'Attest begärd';
    case auditIncidents.certificationRecalled: return 'Attest återkallad';
    case auditIncidents.certificationRejected: return 'Attest avslagen';
    case auditIncidents.certificationAccepted: return 'Attesterad';
    case auditIncidents.certificationClaimRejected: return 'Attest/anspråk avslaget';
    default: return 'Okänd händelse';
  }
}

// The important order is where two incidents have the same timestamp
// Currently this is only known to be an issue for:
// ClaimAccepted and CertificationRequested
const auditIncidentOrder = new Map([
  auditIncidents.ceremonyAccepted,
  auditIncidents.ceremonyReported,
  auditIncidents.ceremonyRejected,
  auditIncidents.ceremonyAccepted,
  auditIncidents.ceremonyReset,
  auditIncidents.timeRequested,
  auditIncidents.timeReported,
  auditIncidents.timeRejected,
  auditIncidents.timeAccepted,
  auditIncidents.timeRecalled,
  auditIncidents.timeRemoved,
  auditIncidents.detachedMission,
  auditIncidents.detachedEvent,
  auditIncidents.claimReported,
  auditIncidents.claimRejected,
  auditIncidents.claimReset,
  auditIncidents.claimAccepted,
  auditIncidents.certificationRequested,
  auditIncidents.certificationRecalled,
  auditIncidents.certificationRejected,
  auditIncidents.certificationAccepted,
  auditIncidents.certificationClaimRejected,
].map((v, i) => [v, i]));

const trimRepeatIncidents = [auditIncidents.ceremonyReported, auditIncidents.timeReported, auditIncidents.claimReported];

export class AuditEntry {
  /**
   * @param {Date} timestamp 
   * @param {string} incident 
   * @param {string?} message 
   * @param {string?} user 
   */
  constructor(timestamp, incident, message, user) {
    this.timestamp = timestamp;
    this.incident = incident;
    this.message = message;
    this.user = user;
  }

  /**
   * @param {AuditData[]} src
   * @param {UserData[]} users
   * @param 
   * @returns {AuditEntry[]}
   */
  static generateAudit(src, users) {
    if (!_.isArray(src)) return [];
    if (src.length === 0) return [];
  
    let userMap = new Map();
    _.forEach(users, x => userMap.set(x.Id, x));
  
    let ordered = _.orderBy(src, [x => x.Timestamp, x => auditIncidentOrder.get(x.Incident)]);
    let filtered = trimRepeats(ordered, x => x.Incident, trimRepeatIncidents);
    return _.map(filtered, x => new AuditEntry(x.Timestamp, getIncidentText(x.Incident), x.Message, getUserName(x, userMap)));
  }
}

class AuditLogController {
}

angular.module("tromanWeb").component('ergoAuditLog', {
  template: require('./auditlog.html'),
  bindings: {
      entries: '<',
  },
  controller: AuditLogController,
});